'use client';
// SEO NOTE: Export this metadata from your /resume/page.js (server component):
// export const metadata = {
//   title: 'Resume – Sahil Chaurasiya | Full-Stack Developer',
//   description: 'View and download the resume of Sahil Chaurasiya – Full-Stack Developer with internship at Shroti Telecom, projects in Next.js, Django, Node.js, and MongoDB. CGPA 7.32, SISTec GN Bhopal.',
//   alternates: { canonical: 'https://portfolio-next-eight-rose.vercel.app/resume' },
//   openGraph: {
//     title: 'Resume – Sahil Chaurasiya | Full-Stack Developer',
//     description: 'Resume of Sahil Chaurasiya: internship, projects, certifications, and technical skills.',
//     url: 'https://portfolio-next-eight-rose.vercel.app/resume',
//   },
// };

import Link from 'next/link';
import Image from 'next/image';
import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Github,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Briefcase,
  Download,
  ArrowLeft,
  ArrowUpRight,
  Trophy,
  Award,
  Globe,
  User,
  ListTree,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Data                                                              */
/* ------------------------------------------------------------------ */

const experience = [
  {
    role: 'Software Developer',
    company: 'ToFly Media',
    type: 'Full-time',
    period: 'Apr 2026 – Present',
    location: 'Bhopal, Madhya Pradesh · On-site',
    current: true,
    accent: '#7c3aed',
    bullets: [
      'First software developer hire — owning the full technical stack across client and internal projects.',
      'Building scalable MERN stack applications from architecture to production deployment.',
      'Designing secure backend systems with JWT authentication and role-based access control.',
      'Building admin panels, client portals, and internal tools for business workflows.',
      'Implementing third-party integrations including payments and real-time features.',
      'Collaborating directly with stakeholders to translate requirements into production-ready systems.',
    ],
  },
  {
    role: 'Junior Software Developer',
    company: 'Shroti Telecom Pvt. Ltd.',
    type: 'Internship',
    period: 'Sep 2025 – Mar 2026',
    location: 'Bhopal, Madhya Pradesh · On-site',
    current: false,
    accent: '#0ea5e9',
    bullets: [
      'Developed and maintained REST APIs using Django and FastAPI for internal and client-facing applications.',
      'Implemented authentication, authorization, and role-based access control for secure data handling.',
      'Debugged API responses and fixed integration issues affecting frontend workflows.',
      'Optimized database queries and improved response consistency.',
      'Worked on feature updates and bug fixes in an existing production codebase.',
    ],
  },
];

const education = [
  {
    school: 'Sagar Institute of Science & Technology (SISTec GN)',
    degree: 'B.Tech. — Computer Science & Engineering',
    year: '2022 – 2026',
    grade: 'CGPA 7.32 / 10',
  },
  {
    school: 'Shri Durga Ji Public School, Azamgarh',
    degree: '12th · CBSE',
    year: '2022',
    grade: '67.60 / 100',
  },
  {
    school: 'Shri Durga Ji Public School, Azamgarh',
    degree: '10th · CBSE',
    year: '2020',
    grade: '79.20 / 100',
  },
];

const projects = [
  {
    title: 'Flowdesk – SaaS Client Portal',
    date: '2026',
    tech: 'React, Vite, Node.js, Express.js, MongoDB, Socket.io, Zustand, JWT, Tailwind CSS',
    desc: 'Full-stack SaaS client portal for performance marketing agencies. Clients track campaigns, view reports, and chat in real time. RBAC with Admin, Manager, Team Member, and Client roles. Built at ToFly Media.',
    link: 'https://flowdesk-drab.vercel.app/',
    teamSize: '1',
  },
  {
    title: 'Office Attendance PWA – GPS-Based Tracking',
    date: 'Apr 2026',
    tech: 'ReactJS, NodeJS, Express.JS, PWA, JWT, Recharts',
    desc: 'Progressive Web App replacing manual check-ins with GPS-based attendance. Haversine geofencing, automated late detection, cron-job auto-checkout, and admin dashboards with CSV export. Deployed at ToFly Media.',
    link: 'https://attendance.toflymediaa.com/',
    teamSize: '1',
  },
  {
    title: 'Developer Portfolio (SEO Optimized)',
    date: 'Nov 2025',
    tech: 'Next.js, Technical SEO, Performance Optimization, Vercel',
    desc: 'Personal developer portfolio with first-page Google rankings for multiple developer-related keywords. Implemented sitemap, robots.txt, structured meta tags, and full performance optimization.',
    link: 'https://sahilchaurasiya.me/',
    teamSize: '1',
  },
  {
    title: 'Sheetal Academy – Business Website & SEO',
    date: 'Jul – Aug 2025',
    tech: 'Next.js, Tailwind CSS, MongoDB, JWT, EmailJS, SEO Optimization, Vercel',
    desc: 'Production website and admin dashboard for a live coaching institute. JWT authentication, dynamic content management, lead capture with automated email responses, and first-page Google rankings.',
    link: 'https://sheetal-academy.vercel.app/',
    teamSize: '1',
  },
  {
    title: 'SISTec Alumni Networking Platform',
    mentor: 'Prof. Nargish Gupta',
    teamSize: '4',
    tech: 'MongoDB, Express.js, React.js, Node.js, Socket.io',
    desc: 'Full-stack networking platform for SISTec students, alumni, and admins. Real-time messaging via Socket.io, discussion forums, events, and job listings. Deployed on the official institute subdomain.',
    link: 'https://alumni.sistec.ac.in/',
  },
  {
    title: 'AI Museum Ticket Booking Automation',
    mentor: 'Dr. Bhavna Gupta',
    teamSize: '6',
    tech: 'Python, Streamlit, MonsterAPI',
    desc: 'Intelligent chatbot automating the entire ticket booking workflow. Automated pricing, ticket generation, and structured user input capture.',
    link: 'https://drive.google.com/drive/folders/1rKAP7_7-eeY-zJDn7uR2JqZKtF89MzVx?usp=drive_link',
  },
];

const skillGroups = [
  { category: 'Languages', skills: ['JavaScript', 'Python', 'C++', 'Core Java', 'SQL'] },
  { category: 'Frontend', skills: ['React.js', 'Next.js', 'Tailwind CSS', 'HTML', 'CSS'] },
  { category: 'Backend', skills: ['Node.js', 'Express.js', 'Django', 'FastAPI', 'REST APIs', 'Socket.io'] },
  { category: 'Databases', skills: ['MongoDB', 'PostgreSQL', 'MySQL'] },
  { category: 'Tools & Others', skills: ['Git', 'GitHub', 'Linux', 'JWT', 'Postman', 'Vercel', 'Streamlit'] },
  { category: 'Core CS', skills: ['DSA', 'DBMS', 'OOP'] },
];

const achievements = [
  'Best Intern Award – Shroti Telecom Pvt. Ltd. (2026)',
  'First Position in Number Games Season 4 · SISTec College',
  'Gold Badge for Problem Solving · CodeChef',
  'Five Star Badges in Java & Python · HackerRank',
  'Five Star Badges in Java, C++ & Python · HackerEarth',
  'Smart India Hackathon · Participant',
  'Selected as Class Representative',
];

const certifications = [
  'Java Skill Test Certification · HackerRank',
  'SQL (Basic) · HackerRank',
  'Java (Basic) · HackerRank',
  'The Joy of Computing using Python · NPTEL',
  'Tools of Trade: Linux and SQL · Google (Coursera)',
  'Climate Change and Human Rights · Amnesty International',
];

const profiles = [
  { name: 'GitHub', url: 'https://github.com/sahil-chaurasiya' },
  { name: 'Portfolio', url: 'https://sahilchaurasiya.me/' },
  { name: 'CodeChef', url: 'https://www.codechef.com/users/sahil73' },
  { name: 'HackerRank', url: 'https://www.hackerrank.com/profile/chaurasiyasahil1' },
  { name: 'HackerEarth', url: 'https://www.hackerearth.com/@chaurasiyasahil18/' },
];

const personalDetails = [
  { label: 'Date of Birth', value: '01 July, 2004' },
  { label: 'Gender', value: 'Male' },
  { label: 'Languages', value: 'English & Hindi' },
  { label: 'Current Address', value: 'SISTec GN, Gandhinagar, Bhopal, MP – 462036' },
  { label: 'Permanent Address', value: 'Tahbarpur, Azamgarh, UP – 276208' },
  { label: 'Email', value: 'chaurasiyasahil18@gmail.com', link: 'mailto:chaurasiyasahil18@gmail.com' },
  { label: 'Phone', value: '+91-8176078586', link: 'tel:+918176078586' },
];

const expertise = [
  'Full-Stack Development',
  'Process Automation',
  'Database Management',
  'Real-time Systems',
  'REST APIs',
  'RBAC',
  'SEO Optimization',
  'Web Applications',
];

const toc = [
  { id: 'summary', label: 'Summary' },
  { id: 'experience', label: 'Experience' },
  { id: 'education', label: 'Education' },
  { id: 'projects', label: 'Projects' },
  { id: 'skills', label: 'Skills' },
  { id: 'achievements', label: 'Achievements' },
  { id: 'certifications', label: 'Certifications' },
  { id: 'profiles', label: 'Online Profiles' },
  { id: 'details', label: 'Personal Details' },
];

const RESUME_FILE = '/Sahil%20Chaurasiya%201.pdf';

/* ------------------------------------------------------------------ */
/*  Small helpers                                                     */
/* ------------------------------------------------------------------ */

const K = ({ children }) => <span className="text-white font-semibold">{children}</span>;

function Section({ id, index, title, children }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '0px 0px -80px 0px' });

  return (
    <motion.section
      id={id}
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, ease: 'easeOut' }}
      className="scroll-mt-28"
    >
      <div className="flex items-baseline gap-3 mb-6">
        <span className="font-mono text-xs text-violet-400/70 tracking-widest">{index}</span>
        <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{title}</h2>
        <span className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
      </div>
      {children}
    </motion.section>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function ResumePage() {
  // Sticky mini identity bar (mobile/tablet only — the sidebar already stays
  // pinned on desktop). The photo, name, and download button each animate
  // into this bar independently, right as the user scrolls past that exact
  // element down in the sidebar — not all at once, and not out of nowhere.
  const [visible, setVisible] = useState({ photo: false, name: false, button: false });
  const photoRef = useRef(null);
  const nameRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const targets = [
      { ref: photoRef, key: 'photo' },
      { ref: nameRef, key: 'name' },
      { ref: buttonRef, key: 'button' },
    ].filter((t) => t.ref.current);
    if (!targets.length) return;

    const keyByNode = new Map(targets.map((t) => [t.ref.current, t.key]));

    const observer = new IntersectionObserver(
      (entries) => {
        setVisible((prev) => {
          const next = { ...prev };
          entries.forEach((entry) => {
            const key = keyByNode.get(entry.target);
            // Element has scrolled up past the trigger line -> reveal its
            // twin in the sticky bar. Scroll back down past it -> hide again.
            if (key) next[key] = !entry.isIntersecting;
          });
          return next;
        });
      },
      { rootMargin: '-80px 0px 0px 0px', threshold: 0 }
    );

    targets.forEach((t) => observer.observe(t.ref.current));
    return () => observer.disconnect();
  }, []);

  const anyVisible = visible.photo || visible.name || visible.button;
  const popIn = {
    initial: { opacity: 0, y: -18, scale: 0.85 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -18, scale: 0.85 },
    transition: { type: 'spring', stiffness: 320, damping: 26 },
  };

  return (
    <main
      className="relative min-h-screen w-full bg-black text-white overflow-x-clip"
      aria-label="Resume of Sahil Chaurasiya – Full-Stack Developer"
    >
      {/* Sticky mini identity bar — sits under the main navbar. Each piece
          (photo / name / download button) flies up and settles in here the
          moment its real counterpart scrolls past that point on the page. */}
      <div className="lg:hidden fixed top-16 left-0 w-full z-40 pointer-events-none">
        <div
          className={`transition-colors duration-300 ${
            anyVisible ? 'bg-black/85 backdrop-blur-md border-b border-white/10' : 'border-b border-transparent'
          }`}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3 min-h-[44px]">
            <AnimatePresence>
              {visible.photo && (
                <motion.div key="mini-photo" {...popIn} className="pointer-events-auto shrink-0">
                  <Image
                    src="/sahil-chaurasiya-full-stack-developer.png"
                    alt="Sahil Chaurasiya – Full-Stack Developer"
                    width={32}
                    height={32}
                    sizes="32px"
                    className="w-8 h-8 object-cover rounded-lg border border-white/10"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {visible.name && (
                <motion.span
                  key="mini-name"
                  {...popIn}
                  className="pointer-events-auto text-sm sm:text-base font-bold text-white truncate"
                >
                  Sahil Chaurasiya
                </motion.span>
              )}
            </AnimatePresence>

            <div className="ml-auto">
              <AnimatePresence>
                {visible.button && (
                  <motion.a
                    key="mini-download"
                    href={RESUME_FILE}
                    download
                    {...popIn}
                    className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold text-black shrink-0 transition-transform hover:scale-[1.03] active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #a78bfa, #38bdf8)' }}
                  >
                    <Download size={13} />
                    Resume
                  </motion.a>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Background — restrained dot grid + two soft glows, consistent with the rest of the site */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '26px 26px',
          }}
        />
        <div
          className="absolute -top-40 -left-40 w-[32rem] h-[32rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.14), transparent 70%)' }}
        />
        <div
          className="absolute bottom-0 right-0 w-[32rem] h-[32rem] rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(14,165,233,0.10), transparent 70%)' }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-24">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-10">
          <Link
            href="/"
            className="group inline-flex items-center gap-2 text-sm font-mono text-white/50 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />
            back
          </Link>
          <a
            href={RESUME_FILE}
            download
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-black transition-transform hover:scale-[1.03] active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, #a78bfa, #38bdf8)' }}
          >
            <Download size={16} />
            Download résumé
          </a>
        </div>

        {/* Hero — identity rendered as a code block, the page's signature element */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <p className="text-xs font-mono tracking-[0.25em] text-violet-400 uppercase mb-4">
            Resume
          </p>
          <h1
            className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight mb-5 bg-clip-text text-transparent"
            style={{ backgroundImage: 'linear-gradient(135deg, #ffffff 40%, #a78bfa 100%)' }}
          >
            Sahil Chaurasiya
          </h1>
          <p className="text-lg sm:text-xl text-white/60 max-w-2xl leading-relaxed mb-7">
            Full-stack developer building scalable, production-grade systems —
            from architecture to deployment.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-white/60 border border-white/10 rounded-full px-3.5 py-1.5">
              <MapPin size={12} className="text-violet-400" />
              Bhopal, India
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-white/60 border border-white/10 rounded-full px-3.5 py-1.5">
              <GraduationCap size={12} className="text-sky-400" />
              CGPA 7.32 / 10
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-white/60 border border-white/10 rounded-full px-3.5 py-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              Open to full-time roles
            </span>
          </div>
        </motion.div>

        {/* Body grid */}
        <div className="grid lg:grid-cols-[280px_1fr] gap-12 lg:gap-16 items-start">
          {/* Sidebar — sticks to the top while scrolling, then naturally releases
              once its own bottom hits the bottom of this grid row (i.e. once the
              main column keeps going past it), so the right column keeps scrolling
              on its own from there. */}
          <aside className="lg:sticky lg:top-24 space-y-10 self-start">
            {/* Identity */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div ref={photoRef} className="relative w-24 h-24 mb-5">
                <Image
                  src="/sahil-chaurasiya-full-stack-developer.png"
                  alt="Sahil Chaurasiya – Full-Stack Developer"
                  width={96}
                  height={96}
                  priority
                  sizes="96px"
                  className="w-full h-full object-cover rounded-xl border border-white/10"
                />
                <span className="absolute -top-1.5 -left-1.5 w-4 h-4 border-t-2 border-l-2 border-violet-400 rounded-tl" />
                <span className="absolute -bottom-1.5 -right-1.5 w-4 h-4 border-b-2 border-r-2 border-sky-400 rounded-br" />
              </div>

              <h1 ref={nameRef} className="text-2xl font-bold text-white mb-1">Sahil Chaurasiya</h1>
              <p className="text-sm text-white/45 mb-5">B.Tech. Computer Science &amp; Engineering</p>

              <div className="space-y-2.5 text-sm text-white/60 mb-6">
                <div className="flex items-center gap-2.5">
                  <MapPin size={14} className="text-violet-400 shrink-0" />
                  Bhopal, MP
                </div>
                <div className="flex items-center gap-2.5">
                  <GraduationCap size={14} className="text-sky-400 shrink-0" />
                  CGPA 7.32 / 10
                </div>
              </div>

              <a
                ref={buttonRef}
                href={RESUME_FILE}
                download
                className="flex items-center justify-center gap-2 w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-black transition-transform hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #a78bfa, #38bdf8)' }}
              >
                <Download size={15} />
                Download résumé
              </a>
            </motion.div>

            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="pt-8 border-t border-white/10"
            >
              <p className="text-xs font-mono tracking-widest text-white/35 uppercase mb-4">Contact</p>
              <div className="space-y-3 text-sm">
                <a
                  href="mailto:chaurasiyasahil18@gmail.com"
                  className="flex items-center gap-2.5 text-white/65 hover:text-white transition-colors break-all"
                >
                  <Mail size={14} className="text-violet-400 shrink-0" />
                  chaurasiyasahil18@gmail.com
                </a>
                <a
                  href="tel:+918176078586"
                  className="flex items-center gap-2.5 text-white/65 hover:text-white transition-colors"
                >
                  <Phone size={14} className="text-sky-400 shrink-0" />
                  +91 81760 78586
                </a>
              </div>
              <div className="flex gap-2.5 mt-5">
                {[
                  { icon: Github, href: 'https://github.com/sahil-chaurasiya', label: 'GitHub' },
                  { icon: Linkedin, href: 'https://www.linkedin.com/in/sahil-chaurasiya-4a2505248/', label: 'LinkedIn' },
                  { icon: Mail, href: 'mailto:chaurasiyasahil18@gmail.com', label: 'Email' },
                  { icon: Phone, href: 'tel:+918176078586', label: 'Phone' },
                ].map(({ icon: Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    target={href.startsWith('http') ? '_blank' : undefined}
                    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    aria-label={label}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition-colors"
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Contents */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="pt-8 border-t border-white/10 hidden lg:block"
            >
              <p className="text-xs font-mono tracking-widest text-white/35 uppercase mb-4 flex items-center gap-2">
                <ListTree size={13} />
                Contents
              </p>
              <ul className="space-y-2.5">
                {toc.map((item, idx) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="group flex items-center gap-3 text-sm text-white/45 hover:text-white transition-colors"
                    >
                      <span className="font-mono text-[11px] text-white/25 group-hover:text-violet-400 transition-colors">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Key expertise */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="pt-8 border-t border-white/10"
            >
              <p className="text-xs font-mono tracking-widest text-white/35 uppercase mb-4">Key expertise</p>
              <div className="flex flex-wrap gap-2">
                {expertise.map((skill) => (
                  <span
                    key={skill}
                    className="text-xs text-white/55 border border-white/10 rounded-full px-3 py-1.5"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          </aside>

          {/* Main content */}
          <div className="space-y-16">
            <Section id="summary" index="01" title="Summary">
              <p className="text-white/65 text-base sm:text-lg leading-relaxed max-w-3xl">
                Full-stack developer currently working at <K>ToFly Media</K> as their first software developer
                hire — responsible for building and shipping production-grade systems end-to-end. Experienced in{' '}
                <K>Node.js</K>, <K>React</K>, <K>Next.js</K>, and <K>MongoDB</K>, with prior internship experience
                using <K>Django</K> and <K>FastAPI</K>. Skilled in <K>RBAC</K>, <K>real-time systems</K>,{' '}
                <K>REST APIs</K>, <K>admin panels</K>, and <K>third-party integrations</K>. Comfortable owning
                complete features from architecture to deployment in a fast-moving environment.
              </p>
            </Section>

            <Section id="experience" index="02" title="Experience">
              <div className="relative space-y-10 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-white/10">
                {experience.map((job) => (
                  <div key={job.role} className="relative pl-8">
                    <span
                      className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-black"
                      style={{ background: job.accent }}
                    />
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-white">{job.role}</h3>
                        {job.current && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/30">
                            Current
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-white/40 font-mono">{job.period}</span>
                    </div>
                    <p className="text-sm font-medium mb-1" style={{ color: job.accent }}>
                      {job.company} · {job.type}
                    </p>
                    <p className="text-xs text-white/40 mb-3">{job.location}</p>
                    <ul className="space-y-2 text-sm text-white/60 leading-relaxed">
                      {job.bullets.map((point) => (
                        <li key={point} className="flex items-start gap-2.5">
                          <span
                            className="mt-1.5 w-1 h-1 rounded-full shrink-0"
                            style={{ background: job.accent }}
                          />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="education" index="03" title="Education">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {education.map((edu) => (
                  <div
                    key={edu.school + edu.year}
                    className="border border-white/10 rounded-xl p-5 hover:border-violet-500/30 transition-colors"
                  >
                    <Briefcase className="hidden" />
                    <p className="font-semibold text-white text-sm mb-2 leading-snug">{edu.school}</p>
                    <p className="text-white/50 text-xs mb-1">{edu.degree}</p>
                    <p className="text-white/30 text-xs font-mono mb-3">{edu.year}</p>
                    <p className="text-sm font-medium text-sky-400">{edu.grade}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="projects" index="04" title="Projects">
              <div className="grid md:grid-cols-2 gap-4">
                {projects.map((project) => (
                  <div
                    key={project.title}
                    className="border border-white/10 rounded-xl p-5 hover:border-sky-500/30 transition-colors flex flex-col"
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      {project.date && (
                        <span className="text-[11px] font-mono text-sky-400">{project.date}</span>
                      )}
                      {(project.mentor || project.teamSize) && (
                        <span className="text-[11px] text-white/30">
                          {project.mentor && `Mentor: ${project.mentor}`}
                          {project.mentor && project.teamSize && ' · '}
                          {project.teamSize && `Team of ${project.teamSize}`}
                        </span>
                      )}
                    </div>
                    <h3 className="text-base font-bold text-white mb-2">{project.title}</h3>
                    <p className="text-[11px] font-mono text-white/35 mb-3 leading-relaxed">{project.tech}</p>
                    <p className="text-sm text-white/60 leading-relaxed mb-4 flex-1">{project.desc}</p>
                    {project.link && (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300 text-sm transition-colors"
                      >
                        View project
                        <ArrowUpRight size={14} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </Section>

            <Section id="skills" index="05" title="Technical Skills">
              <div className="space-y-5">
                {skillGroups.map((group) => (
                  <div key={group.category}>
                    <p className="text-xs font-mono tracking-widest text-white/35 uppercase mb-2.5">
                      {group.category}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {group.skills.map((skill) => (
                        <span
                          key={skill}
                          className="text-sm text-white/70 border border-white/10 rounded-full px-3.5 py-1.5 hover:border-violet-400/40 hover:text-white transition-colors"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            <div className="grid md:grid-cols-2 gap-x-10 gap-y-16">
              <Section id="achievements" index="06" title="Achievements">
                <ul className="space-y-3">
                  {achievements.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-white/60 pb-3 border-b border-white/5 last:border-0"
                    >
                      <Trophy size={15} className="text-amber-400 mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Section>

              <Section id="certifications" index="07" title="Certifications">
                <ul className="space-y-3">
                  {certifications.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-white/60 pb-3 border-b border-white/5 last:border-0"
                    >
                      <Award size={15} className="text-sky-400 mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Section>
            </div>

            <Section id="profiles" index="08" title="Online Profiles">
              <div className="flex flex-wrap gap-3">
                {profiles.map((profile) => (
                  <a
                    key={profile.name}
                    href={profile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-white/65 border border-white/10 rounded-full px-4 py-2 hover:border-violet-400/40 hover:text-white transition-colors"
                  >
                    <Globe size={13} className="text-violet-400" />
                    {profile.name}
                    <ArrowUpRight size={13} className="text-white/30" />
                  </a>
                ))}
              </div>
            </Section>

            <Section id="details" index="09" title="Personal Details">
              <div className="grid sm:grid-cols-2 gap-3">
                {personalDetails.map((detail) => (
                  <div key={detail.label} className="border border-white/10 rounded-xl p-4">
                    <p className="flex items-center gap-2 text-[11px] font-mono text-white/30 uppercase tracking-wide mb-1.5">
                      <User size={11} />
                      {detail.label}
                    </p>
                    {detail.link ? (
                      <a
                        href={detail.link}
                        className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
                      >
                        {detail.value}
                      </a>
                    ) : (
                      <p className="text-sm text-white/75">{detail.value}</p>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </main>
  );
}